import { useMemo, useRef, useState } from "react";

export type SortDir = "asc" | "desc";
export type SortValue = string | number | null;
export type Accessors<T> = Record<string, (row: T) => SortValue>;

// Generic column sorting for a table. Pass an accessors map keyed by column id.
// Nulls always sort last regardless of direction.
export function useSort<T>(rows: T[], accessors: Accessors<T>, initialKey: string, initialDir: SortDir = "desc") {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);

  // Keep latest accessors without making them a memo dependency (they're
  // recreated each render but are functionally stable).
  const accRef = useRef(accessors);
  accRef.current = accessors;

  const sorted = useMemo(() => {
    const acc = accRef.current[sortKey];
    if (!acc) return rows;
    const list = [...rows];
    list.sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === "string" ? av.localeCompare(String(bv)) : (av as number) - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [rows, sortKey, sortDir]);

  function toggle(key: string, defaultDir: SortDir = "desc") {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(defaultDir);
    }
  }

  const arrow = (key: string) => (sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  return { sorted, sortKey, sortDir, toggle, arrow };
}
